<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import RouteBackButton from '@/components/Globals/RouteBackButton.vue'
import MaintenanceDetailComments from '@/components/Maintenances/Body/MaintenanceDetailComments.vue'
import MaintenanceDetailPhotos from '@/components/Maintenances/Body/MaintenanceDetailPhotos.vue'
import { useI18n } from '@/composable/useI18n'
import { useErrorStore } from '@/store/error'
import { useLoadingStore } from '@/store/loading'
import { useMaintenanceStore } from '@/store/maintenance'
import { formatDateWithTime } from '@/utils/date/date-time'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const maintenanceStore = useMaintenanceStore()
const loadingStore = useLoadingStore()
const errorStore = useErrorStore()

const { getCurrentMaintenance } = storeToRefs(maintenanceStore)
const { getMaintenanceById, clearCurrentMaintenance } = maintenanceStore

const maintenanceId = computed(() => Number(route.params.maintenanceId) || 0)
const isLoading = computed(() => loadingStore.loading)
const hasError = computed(() => errorStore.getErrors.length > 0)
const photos = computed(() => getCurrentMaintenance.value?.maintenance_photos ?? [])
const comments = computed(() => getCurrentMaintenance.value?.maintenance_comments ?? [])

function translateStatus(status?: string) {
  if (!status) return '—'
  const key = `maintenance_listing.header.status.${status}`
  return t(key as 'maintenance_listing.header.status.open')
}

function translatePriority(priority?: string) {
  if (!priority) return '—'
  const key = `maintenance_listing.header.priority.${priority}`
  return t(key as 'maintenance_listing.header.priority.low')
}

function translateOrigin(origin?: string) {
  if (!origin) return '—'
  const key = `maintenance_listing.header.origin.${origin}`
  return t(key as 'maintenance_listing.header.origin.manual')
}

function translateRequiresShutdown(value?: boolean) {
  if (value === undefined || value === null) return '—'
  const key = `maintenance_listing.header.requires_shutdown.${String(value)}`
  return t(key as 'maintenance_listing.header.requires_shutdown.true')
}

function priorityBadgeClass(priority?: string) {
  switch (priority) {
    case 'high':
      return 'bg-red/10 text-red'
    case 'medium':
      return 'bg-meta-6/20 text-black'
    case 'low':
      return 'bg-darkGreen/10 text-darkGreen'
    default:
      return 'bg-gray-2 text-body'
  }
}

async function loadMaintenance() {
  if (!maintenanceId.value) {
    router.push({ name: 'maintenance-listing' })
    return
  }
  await getMaintenanceById(maintenanceId.value)
}

function goToListing() {
  router.push({ name: 'maintenance-listing' })
}

onMounted(loadMaintenance)

watch(maintenanceId, () => {
  loadMaintenance()
})

onUnmounted(() => {
  clearCurrentMaintenance()
})
</script>

<template>
  <DefaultLayout>
    <div class="mx-auto max-w-[98%] px-2 py-4">
      <template v-if="isLoading">
        <Loader />
      </template>

      <template v-else-if="hasError || !getCurrentMaintenance">
        <div class="flex flex-col items-center justify-center border border-stroke bg-white py-20 text-center shadow-sm">
          <p class="mb-2 text-lg font-semibold text-black">{{ t('maintenance_detail.errors.not_found_title') }}</p>
          <p class="mb-4 text-sm text-body">{{ t('maintenance_detail.errors.not_found_subtitle') }}</p>
          <button
            type="button"
            class="bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            @click="goToListing">
            {{ t('maintenance_detail.actions.back_to_listing') }}
          </button>
        </div>
      </template>

      <template v-else>
        <div class="mb-4 border border-stroke bg-white p-6 shadow-sm">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex flex-wrap items-center gap-3">
              <RouteBackButton
                :to="{ name: 'maintenance-listing' }"
                :label="t('maintenance_detail.actions.back')"
                :show-label="false" />
              <div>
                <h1 class="text-xl font-bold text-black">
                  {{ t('maintenance_detail.title', { id: getCurrentMaintenance.id }) }}
                </h1>
                <p class="mt-0.5 text-sm text-body">{{ t('maintenance_detail.subtitle') }}</p>
              </div>
            </div>
            <div class="ml-auto flex flex-wrap items-center gap-2">
              <span class="border border-stroke bg-gray-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-black">
                {{ translateStatus(getCurrentMaintenance.status) }}
              </span>
              <span
                class="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
                :class="priorityBadgeClass(getCurrentMaintenance.priority)">
                {{ translatePriority(getCurrentMaintenance.priority) }}
              </span>
            </div>
          </div>
        </div>

        <div class="mb-6 border border-stroke bg-white shadow-sm">
          <div class="border-b border-stroke bg-gray-2 px-4 py-3">
            <h2 class="text-base font-semibold text-black">{{ t('maintenance_detail.info.title') }}</h2>
          </div>
          <div class="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
            <div class="border border-stroke bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.maintenance_type') }}
              </p>
              <p class="mt-1 font-medium text-black">
                {{ getCurrentMaintenance.maintenance_type?.name || '—' }}
              </p>
            </div>
            <div class="border border-stroke bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.user') }}
              </p>
              <p class="mt-1 font-medium text-black">{{ getCurrentMaintenance.user?.name || '—' }}</p>
            </div>
            <div class="border border-stroke bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.sensor') }}
              </p>
              <p class="mt-1 font-medium text-black">{{ getCurrentMaintenance.sensor?.name || '—' }}</p>
            </div>
            <div class="border border-stroke bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.machine') }}
              </p>
              <p class="mt-1 font-medium text-black">{{ getCurrentMaintenance.machine?.name || '—' }}</p>
            </div>
            <div class="border border-stroke bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.origin') }}
              </p>
              <p class="mt-1 font-medium text-black">{{ translateOrigin(getCurrentMaintenance.origin) }}</p>
            </div>
            <div class="border border-stroke bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.requires_shutdown') }}
              </p>
              <p class="mt-1 font-medium text-black">
                {{ translateRequiresShutdown(getCurrentMaintenance.requires_shutdown) }}
              </p>
            </div>
            <div class="border border-stroke bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.started_at') }}
              </p>
              <p class="mt-1 font-medium tabular-nums text-black">
                {{ formatDateWithTime(getCurrentMaintenance.started_at) || '—' }}
              </p>
            </div>
            <div class="border border-stroke bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.finished_at') }}
              </p>
              <p class="mt-1 font-medium tabular-nums text-black">
                {{ formatDateWithTime(getCurrentMaintenance.finished_at) || '—' }}
              </p>
            </div>
            <div class="border border-stroke bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.scheduled_for') }}
              </p>
              <p class="mt-1 font-medium tabular-nums text-black">
                {{ formatDateWithTime(getCurrentMaintenance.scheduled_for) || '—' }}
              </p>
            </div>
            <div class="border border-stroke bg-white p-3 shadow-sm sm:col-span-2 lg:col-span-3">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.description') }}
              </p>
              <p class="mt-1 whitespace-pre-wrap font-medium text-black">
                {{ getCurrentMaintenance.description || '—' }}
              </p>
            </div>
            <div class="border border-stroke bg-white p-3 shadow-sm sm:col-span-2 lg:col-span-3">
              <p class="text-xs font-semibold uppercase tracking-wide text-body">
                {{ t('maintenance_listing.body.table.columns.actions_taken') }}
              </p>
              <p class="mt-1 whitespace-pre-wrap font-medium text-black">
                {{ getCurrentMaintenance.actions_taken || '—' }}
              </p>
            </div>
          </div>
        </div>

        <div class="mb-6">
          <MaintenanceDetailPhotos :photos="photos" />
        </div>

        <MaintenanceDetailComments :comments="comments" />
      </template>
    </div>
  </DefaultLayout>
</template>
